<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import {
    barreDecompte,
    largeurSurEchelle,
    segmentsDessines,
    type CompositionDecompte,
    type OffreDecompte,
    type SegmentBarre,
} from "@core/composables/useCompanyPlafond"
import { ROUTE_SOURCES_FACTUREES } from "@modules/espace-entreprise/components/company/billing/billingSourceCriteres"
import { useDevenirSurvole } from "@core/composables/useDevenirSurvole"

/**
 * Barre de synthèse du forfait (DEV-621, demande de Mathieu du 16/10).
 *
 * INTENTION. Au-dessus du tableau du décompte, le client voit d'un coup
 * d'œil ce qui occupe son forfait : les sources sur son compte qui
 * resteraient facturées s'il les supprimait, celles qui libèrent une place
 * dès la suppression, les sources supprimées encore facturées jusqu'à la fin
 * de la période, puis les places restantes ou l'excédent. Chaque libellé de
 * la légende mène à la page des sources, filtrée sur ces sources. Aucun
 * pourcentage, aucun recalcul : la composition servie fait foi.
 *
 * Chaque segment a la couleur de son devenir (`DEVENIRS_PLACE`), la même que
 * les étiquettes du tableau et de la liste. Survoler (ou parcourir au
 * clavier) une entrée de légende, ou survoler un segment, éclaire ce devenir :
 * l'entrée, le segment et, sous la carte, les lignes du tableau.
 */

// ================================
// TYPES & INTERFACES
// ================================
type Props = {
    /** Composition servie et cohérente (`plafond.composition`) */
    composition: CompositionDecompte
    /** Places du forfait (`plafond.forfait`) */
    forfait: number
    /** Sources facturées servies (`plafond.facturees`) */
    facturees: number
    /** Offre dont la règle s'applique */
    offre: OffreDecompte
    /** Fin de la période en cours */
    fin?: string | Date | null
}

// ================================
// PROPS
// ================================
const props = withDefaults(defineProps<Props>(), { fin: null })

const { survoler, quitter, entrerPointeur, sortirPointeur, estEclaire } =
    useDevenirSurvole()

// ================================
// COMPUTED
// ================================

/** Barre et légende, `null` sans étendue */
const getBarre = computed(() =>
    barreDecompte(
        props.composition,
        props.offre,
        props.forfait,
        props.facturees,
        props.fin
    )
)

/**
 * Segments dessinés : ni la partie vide des places restantes, ni un segment
 * d'étendue nulle (en dépassement, le filet d'un segment que le forfait
 * rempli ne laisse plus dessiner) ; la légende garde tous les segments
 */
const getValeurs = computed(() =>
    segmentsDessines(getBarre.value).map((s) => ({
        label: s.libelle,
        value: s.etendue,
        color: s.couleur,
        devenir: s.devenir,
    }))
)

/**
 * Largeur exacte d'un segment, sans l'arrondi de MeterGroup (un segment
 * d'une source sur 3600 resterait sinon invisible)
 * @param valeur - étendue du segment
 */
function largeur(valeur: number): string {
    return largeurSurEchelle(valeur, getBarre.value?.max ?? 0)
}

/**
 * Destination d'un libellé de la légende : la page des sources filtrée
 * @param segment - segment de la légende
 */
function versSources(segment: SegmentBarre) {
    return { name: ROUTE_SOURCES_FACTUREES, query: { ...segment.filtres } }
}
</script>

<template>
    <div
        v-if="getBarre"
        class="company-subscription-decompte-barre flex flex-col gap-3"
    >
        <BaseMeterGroup
            :value="getValeurs"
            :max="getBarre.max"
            orientation="horizontal"
            class="company-subscription-decompte-barre__barre"
            :pt="{
                meters: 'h-3! rounded-md! bg-surface-200!',
            }"
            aria-label="Occupation du forfait"
        >
            <template #meter="{ value, class: classe }">
                <span
                    :class="[
                        classe,
                        {
                            'company-subscription-decompte-barre__segment--eclaire brightness-85':
                                estEclaire(value.devenir),
                        },
                    ]"
                    class="company-subscription-decompte-barre__segment min-w-0.5 transition-[filter] motion-reduce:transition-none"
                    :style="{
                        backgroundColor: value.color,
                        width: largeur(value.value),
                    }"
                    :data-segment="value.label"
                    :data-devenir="value.devenir"
                    @pointerenter="entrerPointeur($event, value.devenir)"
                    @pointerleave="sortirPointeur"
                />
            </template>
            <!-- légende écrite sous la barre, avec ses liens -->
            <template #label><span class="hidden" /></template>
        </BaseMeterGroup>

        <ul
            class="company-subscription-decompte-barre__legende flex flex-wrap gap-x-6 gap-y-2 text-sm text-surface-700"
        >
            <li
                v-for="segment in getBarre.segments"
                :key="segment.id"
                class="company-subscription-decompte-barre__item -mx-1.5 -my-0.5 flex items-center gap-2 rounded-md px-1.5 py-0.5 transition-colors motion-reduce:transition-none"
                :class="{
                    'company-subscription-decompte-barre__item--eclaire bg-primary-50':
                        estEclaire(segment.devenir),
                }"
                :data-segment="segment.id"
                :data-devenir="segment.devenir"
                @pointerenter="entrerPointeur($event, segment.devenir)"
                @pointerleave="sortirPointeur"
                @focusin="survoler(segment.devenir)"
                @focusout="quitter"
            >
                <span
                    class="company-subscription-decompte-barre__point inline-block size-2.5 shrink-0 rounded-full"
                    :class="{ border: segment.lisere }"
                    :style="{
                        backgroundColor: segment.couleur,
                        borderColor: segment.lisere ?? undefined,
                    }"
                    aria-hidden="true"
                />
                <RouterLink
                    v-if="segment.filtres"
                    :to="versSources(segment)"
                    class="company-subscription-decompte-barre__lien text-primary hover:underline"
                >
                    {{ segment.libelle }}
                </RouterLink>
                <span v-else>{{ segment.libelle }}</span>
                <span
                    class="company-subscription-decompte-barre__nombre font-medium text-surface-800 tabular-nums"
                >
                    {{ segment.nombre }}
                </span>
                <span
                    v-if="
                        segment.id === 'sur_le_compte' &&
                        getBarre.repartitionNonMesuree
                    "
                    class="company-subscription-decompte-barre__non-mesure italic text-surface-500"
                >
                    (répartition non mesurée)
                </span>
            </li>
        </ul>
    </div>
</template>
